/**
 * Brand color → CSS variable token derivation.
 *
 * Trainers (or organizations) pick three brand colors. The web theme
 * exposes many more tokens than three (primary-hover, primary-soft,
 * secondary, etc.). This module bridges the gap: given a base brand
 * color, derive the full token set algorithmically so the trainer
 * never has to think about every state.
 *
 * Why algorithmic and not "let the trainer pick 10 colors": pickers
 * are already hard. Three feels like the most a non-designer can
 * pick confidently. Derivatives are deterministic and stay visually
 * coherent because they're computed from the same hue.
 *
 * Semantic colors (success / warning / error) are NOT re-skinned.
 * Green still means success regardless of brand. The provider only
 * writes the brand tokens.
 */

// Parse `#RRGGBB` or `#RGB` into `{ r, g, b }` (0-255 ints). Returns
// null if the input isn't a valid hex string — caller falls back.
function parseHex(hex) {
  if (typeof hex !== 'string') return null;
  let s = hex.trim();
  if (s.startsWith('#')) s = s.slice(1);
  if (s.length === 3) {
    s = s
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (s.length !== 6) return null;
  const r = parseInt(s.slice(0, 2), 16);
  const g = parseInt(s.slice(2, 4), 16);
  const b = parseInt(s.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b };
}

// Convert RGB ints → HSL floats. Standard formula; lightness/sat in
// the 0..1 range so the darken/lighten arithmetic below stays
// straightforward.
function rgbToHsl({ r, g, b }) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
        break;
      case gn:
        h = ((bn - rn) / d + 2) * 60;
        break;
      default:
        h = ((rn - gn) / d + 4) * 60;
    }
  }
  return { h, s, l };
}

function hue2rgb(p, q, t) {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
}

function hslToHex({ h, s, l }) {
  let r;
  let g;
  let b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hh = h / 360;
    r = hue2rgb(p, q, hh + 1 / 3);
    g = hue2rgb(p, q, hh);
    b = hue2rgb(p, q, hh - 1 / 3);
  }
  const to = (n) =>
    Math.round(Math.max(0, Math.min(255, n * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

// Adjust HSL lightness by `delta` (in 0..1 units). Positive = lighter,
// negative = darker. Clamped to keep results inside [0, 1].
function adjustLightness(hex, delta) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb);
  hsl.l = Math.max(0, Math.min(1, hsl.l + delta));
  return hslToHex(hsl);
}

// Produce `rgba(r, g, b, a)` from a hex color. `a` is 0..1.
function toRgba(hex, alpha) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
}

// WCAG-style relative luminance. Used to pick "on-color" text
// (#fff vs #111) that meets contrast. Threshold ≈ 0.5 lands close
// to AA for most real-world brand palettes.
function relativeLuminance(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return 0.5;
  const ch = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(rgb.r) + 0.7152 * ch(rgb.g) + 0.0722 * ch(rgb.b);
}

function getOnColor(hex) {
  return relativeLuminance(hex) > 0.55 ? '#111827' : '#ffffff';
}

// Validate a hex string. Used at the entry point so a trainer who
// somehow ended up with a corrupted color string just falls back to
// defaults instead of breaking the theme.
export function isValidHex(hex) {
  return parseHex(hex) !== null;
}

// Build the full CSS-variable map from a `{ primary, secondary,
// accent }` triple. Caller writes these directly to
// `document.documentElement.style.setProperty`.
//
// Soft variant uses 10% alpha to match the existing `--primary-soft`
// definition in theme.css. Hover is HSL-darken 6% — gives a
// consistently darker hover regardless of base lightness. Active
// (pressed state) is 12% darker. `on-primary` is the contrast text
// for buttons that paint primary in their background.
export function buildBrandTokens({ primary, secondary, accent } = {}) {
  const tokens = {};
  // Helper for the "--rgb" companion variables the legacy theme used
  // for translucent compositions (`rgba(var(--color-primary-rgb), 0.1)`).
  const rgbTriple = (hex) => {
    const rgb = parseHex(hex);
    return rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : null;
  };

  if (primary && isValidHex(primary)) {
    // Design-system namespace (used everywhere in theme.css and most
    // component CSS modules — this is the canonical surface).
    tokens['--primary'] = primary;
    tokens['--primary-hover'] = adjustLightness(primary, -0.06);
    tokens['--primary-soft'] = toRgba(primary, 0.1);
    tokens['--primary-soft-strong'] = toRgba(primary, 0.18);
    tokens['--on-primary'] = getOnColor(primary);
    // Legacy namespace (used by src/index.css and a handful of
    // older components — kept in sync so we don't need a global CSS
    // migration today).
    tokens['--color-primary'] = primary;
    tokens['--color-primary-hover'] = adjustLightness(primary, -0.1);
    tokens['--color-primary-active'] = adjustLightness(primary, -0.2);
    tokens['--color-primary-light'] = adjustLightness(primary, 0.2);
    tokens['--color-primary-rgb'] = rgbTriple(primary);
  }
  if (secondary && isValidHex(secondary)) {
    tokens['--secondary'] = secondary;
    tokens['--secondary-hover'] = adjustLightness(secondary, -0.06);
    tokens['--secondary-soft'] = toRgba(secondary, 0.12);
    tokens['--on-secondary'] = getOnColor(secondary);
    tokens['--color-secondary'] = secondary;
    tokens['--color-secondary-hover'] = adjustLightness(secondary, -0.1);
    tokens['--color-secondary-active'] = adjustLightness(secondary, -0.2);
    tokens['--color-secondary-light'] = adjustLightness(secondary, 0.2);
    tokens['--color-secondary-rgb'] = rgbTriple(secondary);
  }
  if (accent && isValidHex(accent)) {
    tokens['--accent'] = accent;
    tokens['--accent-soft'] = toRgba(accent, 0.12);
    tokens['--on-accent'] = getOnColor(accent);
    tokens['--color-accent'] = accent;
    tokens['--color-accent-hover'] = adjustLightness(accent, -0.1);
    tokens['--color-accent-active'] = adjustLightness(accent, -0.2);
    tokens['--color-accent-light'] = adjustLightness(accent, 0.2);
    tokens['--color-accent-rgb'] = rgbTriple(accent);
  }
  // Optional gradient companions kept for back-compat with the
  // legacy ThemeManager. Only set when we have at least two colors
  // to compose between.
  if (primary && secondary && isValidHex(primary) && isValidHex(secondary)) {
    tokens['--gradient-primary-secondary'] =
      `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`;
  }
  if (primary && accent && isValidHex(primary) && isValidHex(accent)) {
    tokens['--gradient-primary-accent'] =
      `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`;
  }
  return tokens;
}

// List of var names the provider owns. Used on unmount to remove our
// inline overrides so the stylesheet defaults take over again.
export const BRAND_TOKEN_NAMES = [
  // Design-system namespace
  '--primary',
  '--primary-hover',
  '--primary-soft',
  '--primary-soft-strong',
  '--on-primary',
  '--secondary',
  '--secondary-hover',
  '--secondary-soft',
  '--on-secondary',
  '--accent',
  '--accent-soft',
  '--on-accent',
  // Legacy namespace (kept in sync via buildBrandTokens)
  '--color-primary',
  '--color-primary-hover',
  '--color-primary-active',
  '--color-primary-light',
  '--color-primary-rgb',
  '--color-secondary',
  '--color-secondary-hover',
  '--color-secondary-active',
  '--color-secondary-light',
  '--color-secondary-rgb',
  '--color-accent',
  '--color-accent-hover',
  '--color-accent-active',
  '--color-accent-light',
  '--color-accent-rgb',
  '--gradient-primary-secondary',
  '--gradient-primary-accent',
];
